import { useEffect } from 'react'

/**
 * Scrollbars are hidden app-wide, so a horizontally scrollable row needs
 * another way to say "there is more this way". This marks each scroller with
 * data-overflow="start" | "end" | "both" so CSS can fade the right edges, and
 * removes the attribute when everything already fits — a fade over a row that
 * does not scroll is a false signal.
 */
export function useScrollHint(selector: string, deps: unknown[] = []) {
  useEffect(() => {
    // the attribute lands on the nearest marked wrapper when there is one, so
    // the fade can be positioned against it without needing :has()
    const target = (el: HTMLElement) => el.closest<HTMLElement>('[data-scroll-hint]') || el
    const update = () => {
      document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        const slack = el.scrollWidth - el.clientWidth
        if (slack <= 4) {
          target(el).removeAttribute('data-overflow')
          return
        }
        const atStart = el.scrollLeft <= 2
        const atEnd = el.scrollLeft >= slack - 2
        target(el).setAttribute('data-overflow', atStart ? 'end' : atEnd ? 'start' : 'both')
      })
    }

    update()
    const frame = window.setTimeout(update, 120)
    const scrollers = Array.from(document.querySelectorAll<HTMLElement>(selector))
    scrollers.forEach((el) => el.addEventListener('scroll', update, { passive: true }))
    window.addEventListener('resize', update)
    return () => {
      window.clearTimeout(frame)
      scrollers.forEach((el) => el.removeEventListener('scroll', update))
      window.removeEventListener('resize', update)
    }
  // The spread is the hook's contract: callers pass what should re-measure.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, ...deps])
}
